import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import SaveToModal from '../../modals/SaveToModal/SaveToModal';
import CreateSavedListModal from '../../modals/CreateSavedListModal/CreateSavedListModal';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsIcon from '@mui/icons-material/Directions';
import ProviderCard from '../../provider/ProviderCard/ProviderCard';
import ProcedureCard from '../../procedure/ProcedureCard/ProcedureCard';
import MedicationCard from '../../medication/MedicationCard/MedicationCard';
import { aiFollowupApi, ApiError } from '../../../services/api';
import styles from './AISearchResultDetail.module.css';

interface Provider {
  id: string;
  name: string;
  specialty?: string;
  distance?: string;
  rating?: number;
  reviewCount?: number;
  imageUrl?: string;
  address?: string;
  isInNetwork?: boolean;
  estimatedCost?: string;
  availability?: string;
  phone?: string;
  acceptsInsurance?: boolean;
  nextAvailable?: string;
}

interface Treatment {
  id: string;
  name: string;
  type?: string;
  description?: string;
  effectiveness?: string;
  duration?: string;
  category?: string;
  averageCost?: string;
  insuranceCoverage?: string;
  outOfPocket?: string;
  preparationSteps?: string[];
  priceRange?: {
    min: number;
    max: number;
  };
  averagePrice?: number;
  providers?: number;
}

interface Medication {
  id: string;
  name: string;
  type?: string;
  genericName?: string;
  dosage?: string;
  estimatedCost?: string;
  monthlySupply?: string;
  insuranceCoverage?: string;
  copay?: string;
  coveredByInsurance?: boolean;
  warnings?: string[];
  alternatives?: string[];
  category?: string;
  priceRange?: {
    min: number;
    max: number;
  };
  averagePrice?: number;
  commonDosages?: string[];
  description?: string;
  brandNames?: string[];
}

interface CarePlan {
  id: string;
  summary: string;
  urgencyLevel: 'low' | 'medium' | 'high';
  recommendedActions: any[];
  preventiveMeasures: string[];
  followUpInstructions: string;
  warnings?: string[];
  createdAt: string;
}

const AISearchResultDetail: FunctionComponent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isItemSaved } = useSavedItems();
  const [activeTab, setActiveTab] = useState<'overview' | 'providers' | 'treatments' | 'costs'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [carePlanStatus, setCarePlanStatus] = useState<'generating' | 'ready' | 'error' | 'timeout'>('generating');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [carePlan, setCarePlan] = useState<CarePlan | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [costEstimates, setCostEstimates] = useState<any>(null);
  const [generationProgress, setGenerationProgress] = useState({
    progress: 0,
    currentStep: '',
    completedSteps: [] as string[],
    steps: [] as string[]
  });

  const query = searchParams.get('q') || '';
  const refined = searchParams.get('refined') === 'true';
  const sessionId = searchParams.get('sessionId') || '';
  
  const carePlanId = carePlan?.id || `care-plan-${Date.now()}`;
  const isSaved = isItemSaved(carePlanId, 'care-plan');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Fetch care plan from server with polling support
    const fetchCarePlan = async () => {
      if (!sessionId) {
        // If no sessionId, navigate back to AI search
        navigate('/ai-search');
        return;
      }

      let retryCount = 0;
      const maxRetries = 60; // Poll for up to 60 seconds
      const pollInterval = 1000; // Poll every 1 second

      const pollForCarePlan = async (): Promise<boolean> => {
        try {
          const result = await aiFollowupApi.getCarePlan(sessionId);
          
          if (result.success && result.data) {
            const data = result.data;
            setCarePlan({
              summary: data.summary,
              urgencyLevel: data.urgencyLevel,
              recommendedActions: data.recommendedActions,
              preventiveMeasures: data.preventiveMeasures,
              followUpInstructions: data.followUpInstructions,
              id: data.id,
              createdAt: data.createdAt
            });
            setProviders(data.providers || []);
            setTreatments(data.treatments || []);
            setMedications(data.medications || []);
            setCostEstimates(data.estimatedCosts || null);
            setCarePlanStatus('ready');
            setIsLoading(false);
            return true;
          }
        } catch (error: any) {
          console.error('Error fetching care plan:', error);
          
          // If we get a 202 status, the care plan is still generating
          if (error instanceof ApiError && error.status === 202) {
            console.log('Care plan still generating, will retry...');
            // Extract progress information from error response
            const errorData = error.data || {};
            if (errorData.progress !== undefined) {
              setGenerationProgress({
                progress: errorData.progress,
                currentStep: errorData.currentStep || '',
                completedSteps: errorData.completedSteps || [],
                steps: errorData.steps || []
              });
            }
            return false;
          }
          
          // For other errors, stop polling
          console.error('API Error:', error.status, error.message);
          setCarePlanStatus('error');
          setIsLoading(false);
          return true; // Stop polling
        }
        
        return false;
      };

      // Initial attempt
      const success = await pollForCarePlan();
      
      if (!success && retryCount < maxRetries) {
        // Start polling
        const pollTimer = setInterval(async () => {
          retryCount++;
          const done = await pollForCarePlan();
          
          if (done || retryCount >= maxRetries) {
            clearInterval(pollTimer);
            
            if (retryCount >= maxRetries && !done) {
              console.error('Timed out waiting for care plan');
              setCarePlanStatus('timeout');
              setIsLoading(false);
            }
          }
        }, pollInterval);
      }
    };

    fetchCarePlan();
  }, [sessionId, navigate]);

  const handleSave = () => {
    setShowSaveModal(true);
  };

  const handleShare = async () => {
    if (navigator.share && carePlan) {
      try {
        await navigator.share({
          title: `Care Plan: ${query}`,
          text: carePlan.summary,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled the share dialog or an error occurred
        // Silently handle the error - no need to show anything to the user
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback for browsers that don't support native share
      console.log('Web Share API not supported');
      // Could implement a custom share modal here
    }
  };

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case 'high':
        return styles.urgencyHigh;
      case 'medium':
        return styles.urgencyMedium;
      case 'low':
        return styles.urgencyLow;
      default:
        return '';
    }
  };

  const getUrgencyIcon = (level: string) => {
    switch (level) {
      case 'high':
        return <WarningIcon />;
      case 'medium':
        return <InfoIcon />;
      case 'low':
        return <CheckCircleIcon />;
      default:
        return null;
    }
  };

  if (isLoading || carePlanStatus === 'generating') {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p className={styles.loadingText}>
          <AutoAwesomeIcon className={styles.aiIcon} />
          {carePlanStatus === 'generating' 
            ? 'AI is creating your personalized care plan...'
            : 'AI is analyzing your information...'}
        </p>
        {carePlanStatus === 'generating' && (
          <div className={styles.generatingSteps}>
            <div className={styles.step}>
              <div className={styles.stepIcon}>✓</div>
              <span>Analyzed your symptoms</span>
            </div>
            <div className={styles.step}>
              <div className={styles.stepIcon}>✓</div>
              <span>Gathered additional information</span>
            </div>
            {generationProgress.steps.length > 0 ? (
              generationProgress.steps.map((step, index) => {
                const isCompleted = generationProgress.completedSteps.includes(step);
                const isActive = step === generationProgress.currentStep;
                const className = isCompleted ? styles.step : (isActive ? `${styles.step} ${styles.active}` : `${styles.step} ${styles.pending}`);
                
                return (
                  <div key={index} className={className}>
                    <div className={styles.stepIcon}>
                      {isCompleted ? '✓' : isActive ? (
                        <div className={styles.stepSpinner} />
                      ) : '○'}
                    </div>
                    <span>{step}</span>
                  </div>
                );
              })
            ) : (
              <div className={styles.step + ' ' + styles.active}>
                <div className={styles.stepIcon}>
                  <div className={styles.stepSpinner} />
                </div>
                <span>Creating personalized care plan</span>
              </div>
            )}
          </div>
        )}
        {generationProgress.progress > 0 && (
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${generationProgress.progress}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if (carePlanStatus === 'error' || carePlanStatus === 'timeout') {
    return (
      <div className={styles.errorContainer}>
        <WarningIcon className={styles.errorIcon} />
        <h2>{carePlanStatus === 'timeout' ? 'Care Plan Generation Taking Longer Than Expected' : 'Unable to Generate Care Plan'}</h2>
        <p>
          {carePlanStatus === 'timeout' 
            ? 'The care plan is taking longer than usual to generate. This can happen with complex medical queries. Please try again or simplify your question.'
            : 'We encountered an issue while creating your care plan. Please try again.'}
        </p>
        <button 
          className={styles.retryButton}
          onClick={() => navigate('/ai-search')}
        >
          Start Over
        </button>
      </div>
    );
  }

  if (!carePlan) {
    return null;
  }

  return (
    <div className={styles.aiSearchResultDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Your Care Plan</h1>
        <div className={styles.headerActions}>
          <button className={styles.saveButton} onClick={handleSave}>
            {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </button>
          <button className={styles.shareButton} onClick={handleShare}>
            <ShareIcon />
          </button>
        </div>
      </div>

      <div className={styles.aiLabel}>
        <AutoAwesomeIcon className={styles.aiIcon} />
        AI-Powered Personalized Recommendations
      </div>

      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${activeTab === 'overview' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'providers' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('providers')}
        >
          Providers
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'treatments' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('treatments')}
        >
          Treatments
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'costs' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('costs')}
        >
          Costs
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'overview' && (
          <div className={styles.overviewTab}>
            <div className={styles.summaryCard}>
              <div className={`${styles.urgencyBadge} ${getUrgencyColor(carePlan.urgencyLevel)}`}>
                {getUrgencyIcon(carePlan.urgencyLevel)}
                <span>{carePlan.urgencyLevel.charAt(0).toUpperCase() + carePlan.urgencyLevel.slice(1)} Priority</span>
              </div>
              <p className={styles.summary}>{carePlan.summary}</p>
            </div>

            {carePlan.warnings && carePlan.warnings.length > 0 && (
              <div className={styles.warningCard}>
                <h3 className={styles.warningTitle}>
                  <WarningIcon />
                  Important Warnings
                </h3>
                <ul className={styles.warningList}>
                  {carePlan.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Recommended Actions</h2>
              <ul className={styles.actionList}>
                {carePlan.recommendedActions.map((action, index) => (
                  <li key={action.id || index} className={styles.actionItem}>
                    <CheckCircleIcon className={styles.actionIcon} />
                    <div>
                      <span>{action.action || action}</span>
                      {action.description && (
                        <p className={styles.actionDescription}>{action.description}</p>
                      )}
                      {action.timeframe && (
                        <p className={styles.actionTimeframe}>{action.timeframe}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Preventive Measures</h2>
              <div className={styles.preventiveGrid}>
                {carePlan.preventiveMeasures.map((measure, index) => (
                  <div key={index} className={styles.preventiveCard}>
                    <InfoIcon className={styles.preventiveIcon} />
                    <p>{measure}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.followUpCard}>
              <CalendarTodayIcon className={styles.followUpIcon} />
              <div>
                <h3 className={styles.followUpTitle}>Follow-Up Recommendation</h3>
                <p className={styles.followUpText}>{carePlan.followUpInstructions}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'providers' && (
          <div className={styles.providersTab}>
            <p className={styles.tabDescription}>
              Recommended healthcare providers based on your condition and insurance coverage
            </p>
            <div className={styles.providerList}>
              {providers.map((provider) => (
                <div key={provider.id} className={styles.providerCardWrapper}>
                  <ProviderCard provider={provider} />
                  <div className={styles.providerActions}>
                    <button className={styles.scheduleButton}>
                      <CalendarTodayIcon />
                      Schedule Appointment
                    </button>
                    <button className={styles.callButton}>
                      <PhoneIcon />
                      Call
                    </button>
                    <button className={styles.directionsButton}>
                      <DirectionsIcon />
                      Directions
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'treatments' && (
          <div className={styles.treatmentsTab}>
            <div className={styles.treatmentSection}>
              <h3 className={styles.treatmentTitle}>Recommended Procedures</h3>
              <div className={styles.procedureList}>
                {treatments.filter(t => t.type !== 'Lifestyle').map((treatment) => (
                  <div key={treatment.id} className={styles.procedureWrapper}>
                    <ProcedureCard procedure={treatment} />
                    {treatment.preparationSteps && (
                      <div className={styles.preparationSteps}>
                        <h4>Preparation Steps:</h4>
                        <ul>
                          {treatment.preparationSteps.map((step, index) => (
                            <li key={index}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.treatmentSection}>
              <h3 className={styles.treatmentTitle}>Recommended Medications</h3>
              <div className={styles.medicationList}>
                {medications.map((medication) => (
                  <MedicationCard key={medication.id} medication={medication} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'costs' && costEstimates && (
          <div className={styles.costsTab}>
            <div className={styles.costSummaryCard}>
              <h3 className={styles.costTitle}>Estimated Total Costs</h3>
              <div className={styles.costBreakdown}>
                {costEstimates.consultations && (
                  <>
                    <div className={styles.costRow}>
                      <span>Primary Care Visit</span>
                      <span className={styles.costAmount}>
                        ${Math.round(costEstimates.consultations.primaryCare.min * (1 - costEstimates.consultations.primaryCare.covered / 100))}-
                        ${Math.round(costEstimates.consultations.primaryCare.max * (1 - costEstimates.consultations.primaryCare.covered / 100))}
                      </span>
                    </div>
                    {costEstimates.consultations.specialist && (
                      <div className={styles.costRow}>
                        <span>Specialist Visit</span>
                        <span className={styles.costAmount}>
                          ${Math.round(costEstimates.consultations.specialist.min * (1 - costEstimates.consultations.specialist.covered / 100))}-
                          ${Math.round(costEstimates.consultations.specialist.max * (1 - costEstimates.consultations.specialist.covered / 100))}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {costEstimates.medications && (
                  <div className={styles.costRow}>
                    <span>Medications (monthly)</span>
                    <span className={styles.costAmount}>
                      ${Math.round(costEstimates.medications.monthly.min * (1 - costEstimates.medications.monthly.covered / 100))}-
                      ${Math.round(costEstimates.medications.monthly.max * (1 - costEstimates.medications.monthly.covered / 100))}
                    </span>
                  </div>
                )}
                {costEstimates.treatments && costEstimates.treatments.therapy && (
                  <div className={styles.costRow}>
                    <span>Therapy ({costEstimates.treatments.therapy.sessions} sessions)</span>
                    <span className={styles.costAmount}>
                      ${Math.round(costEstimates.treatments.therapy.min * costEstimates.treatments.therapy.sessions * (1 - costEstimates.treatments.therapy.covered / 100))}-
                      ${Math.round(costEstimates.treatments.therapy.max * costEstimates.treatments.therapy.sessions * (1 - costEstimates.treatments.therapy.covered / 100))}
                    </span>
                  </div>
                )}
                <div className={styles.costDivider} />
                <div className={styles.costRow}>
                  <span className={styles.totalLabel}>Estimated Total (with insurance)</span>
                  <span className={styles.totalAmount}>
                    ${costEstimates.totalEstimated.withInsurance.min}-${costEstimates.totalEstimated.withInsurance.max}
                  </span>
                </div>
              </div>
              <p className={styles.costNote}>
                <InfoIcon />
                These are estimates based on your insurance coverage. Actual costs may vary.
              </p>
            </div>

            <div className={styles.savingsCard}>
              <h3 className={styles.savingsTitle}>
                <AttachMoneyIcon />
                Ways to Save
              </h3>
              <ul className={styles.savingsList}>
                <li>Use in-network providers for lower copays</li>
                <li>Ask about generic medication alternatives</li>
                <li>Check if your insurance covers preventive care at 100%</li>
                <li>Consider telemedicine for follow-up appointments</li>
                <li>Use FSA/HSA funds for medical expenses</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className={styles.bottomActions}>
        <button 
          className={styles.modifyButton}
          onClick={() => navigate(`/ai-search-followup?q=${encodeURIComponent(query)}`)}
        >
          Modify Answers
        </button>
        <button className={styles.newSearchButton} onClick={() => navigate('/ai-search')}>
          New Search
        </button>
      </div>

      <SaveToModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        item={{
          id: carePlanId,
          type: 'care-plan',
          title: `Care Plan: ${query}`,
          subtitle: `${carePlan?.urgencyLevel || 'medium'} priority - ${carePlan?.recommendedActions?.length || 0} recommendations`,
          data: {
            query,
            carePlan,
            providers,
            treatments,
            medications
          }
        }}
        onCreateNewList={() => {
          setShowSaveModal(false);
          setShowCreateListModal(true);
        }}
      />

      <CreateSavedListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onListCreated={(listId) => {
          navigate(`/list/${listId}`);
        }}
      />
    </div>
  );
};

export default AISearchResultDetail;